import api from '../../api';
import shopify from '../../shopify';

export default {
  name: 'accountAddresses',
  component(liquidAddresses = [], defaultCountry = 'AU') {
    // Helper function to sort addresses with default first
    const sortAddresses = (addresses) => {
      if (!addresses || !Array.isArray(addresses)) return [];
      return [...addresses].sort((a, b) => {
        if (a.default && !b.default) return -1;
        if (!a.default && b.default) return 1;
        return 0;
      });
    };

    return {
      loading: false,
      error: null,
      success: false,
      deleteError: null,
      addresses: sortAddresses(liquidAddresses), // For display from Liquid
      fetchedAddresses: [], // For API operations with GraphQL IDs
      hasUpdates: false, // Track if we should use fetchedAddresses for display
      isUpdating: false, // Track when address operations are happening
      isEditMode: false,
      editingAddress: null,
      showForm: false,
      countries: [],
      provinces: [],
      defaultCountry: defaultCountry,
      
      formData: {
        id: null,
        firstName: '',
        lastName: '',
        company: '',
        address1: '',
        address2: '',
        city: '',
        province: '',
        zip: '',
        country: '',
        phone: '',
        default: false
      },

      async init() {
        await this.fetchAddresses();
        await this.fetchCountries();
      },

      async fetchAddresses() {
        this.loading = true;
        this.error = null;

        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
          this.error = 'No access token found';
          this.loading = false;
          return;
        }

        try {
          const query = `
            query {
              customer(customerAccessToken: "${accessToken}") {
                id
                defaultAddress {
                  id
                  address1
                  address2
                  city
                  company
                  country
                  countryCodeV2
                  firstName
                  lastName
                  name
                  phone
                  province
                  provinceCode
                  zip
                }
                addresses(first: 10) {
                  edges {
                    node {
                      id
                      address1
                      address2
                      city
                      company
                      country
                      countryCodeV2
                      firstName
                      lastName
                      name
                      phone
                      province
                      provinceCode
                      zip
                    }
                  }
                }
              }
            }
          `;

          const response = await fetch('/api/2025-07/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
            },
            body: JSON.stringify({ query })
          });

          if (!response.ok) {
            throw new Error('Failed to fetch addresses');
          }

          const { data, errors } = await response.json();

          if (errors) {
            throw new Error(errors[0].message);
          }

          const customer = data.customer;
          const customerAddresses = customer?.addresses.edges.map(edge => edge.node) || [];
          const defaultAddress = customer?.defaultAddress;

          // Mark default address
          customerAddresses.forEach(address => {
            address.default = defaultAddress && address.id === defaultAddress.id;
          });

          // Store fetched addresses for API operations
          this.fetchedAddresses = customerAddresses.sort((a, b) => {
            if (a.default && !b.default) return -1;
            if (!a.default && b.default) return 1;
            return 0;
          });

          // If there have been updates, use fetchedAddresses for display
          if (this.hasUpdates) {
            this.addresses = this.convertToLiquidFormat(this.fetchedAddresses);
          }

        } catch (error) {
          console.error('Error fetching addresses:', error);
          this.error = error.message;
        } finally {
          this.loading = false;
        }
      },

      async fetchCountries() {
        try {
          const countries = await shopify.getCountries();
          this.countries = countries.map(country => ({
            code: country.code,
            name: country.name
          }));
        } catch (error) {
          console.error('Error fetching countries:', error);
          // Fallback to basic countries if API fails
          this.countries = [
            { code: 'US', name: 'United States' },
            { code: 'CA', name: 'Canada' },
            { code: 'GB', name: 'United Kingdom' },
            { code: 'AU', name: 'Australia' }
          ];
        }
      },

      async fetchProvinces(countryCode) {
        if (!countryCode) {
          this.provinces = [];
          return;
        }
        
        try {
          const provinces = await shopify.getProvinces(countryCode);
          this.provinces = provinces.map(province => ({
            code: province.code,
            name: province.name
          }));
        } catch (error) {
          console.error('Error fetching provinces:', error);
          this.provinces = [];
        }
      },

      async showAddForm() {
        this.isEditMode = false;
        this.showForm = true;
        this.resetFormData();
        this.formData.country = this.defaultCountry;
        
        // Fetch provinces for the default country
        await this.fetchProvinces(this.defaultCountry);
      },

      async showEditForm(address) {
        this.isEditMode = true;
        this.showForm = true;
        this.editingAddress = address;
        this.populateFormData(address);
        
        // Fetch provinces for the address's country
        if (address.country_code) {
          await this.fetchProvinces(address.country_code);
        }
      },

      hideForm() {
        this.showForm = false;
        this.isEditMode = false;
        this.editingAddress = null;
        this.resetFormData();
        this.error = null;
        this.success = false;
      },

      resetFormData() {
        this.formData = {
          id: null,
          firstName: '',
          lastName: '',
          company: '',
          address1: '',
          address2: '',
          city: '',
          province: '',
          zip: '',
          country: this.defaultCountry,
          phone: '',
          default: false
        };
      },

      populateFormData(address) {
        console.log(address)
        // Find the corresponding GraphQL address to get the proper ID
        const graphqlAddress = this.findGraphQLAddress(address);
        
        this.formData = {
          id: graphqlAddress?.id || null,
          firstName: address.first_name || '',
          lastName: address.last_name || '',
          company: address.company || '',
          address1: address.address1 || '',
          address2: address.address2 || '',
          city: address.city || '',
          province: address.province || '',
          zip: address.zip || '',
          country: address.country_code || '',
          phone: address.phone || '',
          default: address.default || false
        };
      },

      // Helper method to find corresponding GraphQL address by matching address fields
      findGraphQLAddress(liquidAddress) {
        return this.fetchedAddresses.find(fetchedAddr => 
          fetchedAddr.address1 === liquidAddress.address1 &&
          fetchedAddr.city === liquidAddress.city &&
          fetchedAddr.zip === liquidAddress.zip &&
          fetchedAddr.firstName === liquidAddress.first_name &&
          fetchedAddr.lastName === liquidAddress.last_name
        );
      },

      // Helper method to get GraphQL ID for operations
      getGraphQLId(liquidAddress) {
        const graphqlAddress = this.findGraphQLAddress(liquidAddress);
        return graphqlAddress?.id;
      },

      // Convert GraphQL addresses to Liquid format for display
      convertToLiquidFormat(graphqlAddresses) {
        const converted = graphqlAddresses.map(addr => ({
          id: addr.id,
          first_name: addr.firstName,
          last_name: addr.lastName,
          company: addr.company,
          address1: addr.address1,
          address2: addr.address2,
          city: addr.city,
          province: addr.province,
          zip: addr.zip,
          country: addr.country,
          country_code: addr.countryCodeV2,
          phone: addr.phone,
          default: addr.default
        }));
        return sortAddresses(converted);
      },

      async saveAddress() {
        this.loading = true;
        this.isUpdating = true;
        this.error = null;
        this.success = false;

        try {
          if (this.isEditMode) {
            await this.updateAddress();
          } else {
            await this.addAddress();
          }

          this.success = true;
          this.hasUpdates = true;
          await this.fetchAddresses();
          
          // Show success message for 2 seconds before hiding form
          setTimeout(() => {
            this.hideForm();
          }, 2000);
          
        } catch (error) {
          console.error('Error saving address:', error);
          this.error = error.message;
        } finally {
          this.loading = false;
          this.isUpdating = false;
        }
      },

      async addAddress() {
        const accessToken = localStorage.getItem('accessToken');
        const isDefault = this.formData.default;
        
        const addressData = { ...this.formData };
        delete addressData.default;
        delete addressData.id;

        const mutation = `
          mutation customerAddressCreate($address: MailingAddressInput!, $customerAccessToken: String!) {
            customerAddressCreate(address: $address, customerAccessToken: $customerAccessToken) {
              customerAddress {
                id
              }
              customerUserErrors {
                code
                field
                message
              }
              userErrors {
                field
                message
              }
            }
          }
        `;

        const response = await fetch('/api/2025-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
          },
          body: JSON.stringify({
            query: mutation,
            variables: {
              address: addressData,
              customerAccessToken: accessToken
            }
          })
        });

        const result = await response.json();

        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        const { customerAddressCreate } = result.data;
        
        if (customerAddressCreate.userErrors?.length > 0) {
          throw new Error(customerAddressCreate.userErrors[0].message);
        }

        // Set as default if requested
        if (isDefault && customerAddressCreate.customerAddress) {
          await this.setDefaultAddress(customerAddressCreate.customerAddress.id);
        }
      },

      async updateAddress() {
        const accessToken = localStorage.getItem('accessToken');
        const isDefault = this.formData.default;
        
        const addressData = { ...this.formData };
        delete addressData.default;
        const addressId = addressData.id;
        delete addressData.id;

        const mutation = `
          mutation customerAddressUpdate($address: MailingAddressInput!, $customerAccessToken: String!, $id: ID!) {
            customerAddressUpdate(address: $address, customerAccessToken: $customerAccessToken, id: $id) {
              customerAddress {
                id
              }
              customerUserErrors {
                code
                field
                message
              }
              userErrors {
                field
                message
              }
            }
          }
        `;

        const response = await fetch('/api/2025-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
          },
          body: JSON.stringify({
            query: mutation,
            variables: {
              address: addressData,
              customerAccessToken: accessToken,
              id: addressId
            }
          })
        });

        const result = await response.json();

        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        const { customerAddressUpdate } = result.data;
        
        if (customerAddressUpdate.userErrors?.length > 0) {
          throw new Error(customerAddressUpdate.userErrors[0].message);
        }

        // Set as default if requested
        if (isDefault) {
          await this.setDefaultAddress(addressId);
        }
      },

      async setDefaultAddress(addressId) {
        const accessToken = localStorage.getItem('accessToken');
        
        const mutation = `
          mutation customerDefaultAddressUpdate($addressId: ID!, $customerAccessToken: String!) {
            customerDefaultAddressUpdate(addressId: $addressId, customerAccessToken: $customerAccessToken) {
              customer {
                id
              }
              customerUserErrors {
                code
                field
                message
              }
              userErrors {
                field
                message
              }
            }
          }
        `;

        const response = await fetch('/api/2025-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
          },
          body: JSON.stringify({
            query: mutation,
            variables: {
              addressId: addressId,
              customerAccessToken: accessToken
            }
          })
        });

        const result = await response.json();

        if (result.errors) {
          throw new Error(result.errors[0].message);
        }
      },

      async removeAddress(addressId) {
        if (!confirm('Are you sure you want to delete this address?')) {
          return;
        }

        this.deleteError = null;
        this.isUpdating = true;
        
        try {
          const accessToken = localStorage.getItem('accessToken');
          
          const mutation = `
            mutation customerAddressDelete($customerAccessToken: String!, $id: ID!) {
              customerAddressDelete(customerAccessToken: $customerAccessToken, id: $id) {
                customerUserErrors {
                  code
                  message
                }
                deletedCustomerAddressId
                userErrors {
                  field
                  message
                }
              }
            }
          `;

          const response = await fetch('/api/2025-07/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
            },
            body: JSON.stringify({
              query: mutation,
              variables: {
                customerAccessToken: accessToken,
                id: addressId
              }
            })
          });

          const result = await response.json();

          if (result.errors) {
            throw new Error('Cannot delete the default address.');
          }

          const { customerAddressDelete } = result.data;
          
          if (customerAddressDelete.userErrors?.length > 0) {
            throw new Error(customerAddressDelete.userErrors[0].message);
          }

          this.hasUpdates = true;
          await this.fetchAddresses();
          
        } catch (error) {
          console.error('Error deleting address:', error);
          this.deleteError = error.message;
          
          setTimeout(() => {
            this.deleteError = null;
          }, 5000);
        } finally {
          this.isUpdating = false;
        }
      },

      async makeDefault(addressId) {
        this.isUpdating = true;
        
        try {
          await this.setDefaultAddress(addressId);
          this.hasUpdates = true;
          await this.fetchAddresses();
        } catch (error) {
          console.error('Error setting default address:', error);
          this.error = error.message;
        } finally {
          this.isUpdating = false;
        }
      },

      async onCountryChange() {
        this.formData.province = '';
        await this.fetchProvinces(this.formData.country);
      }
    };
  }
};